
import { useState, useRef, useEffect } from 'react';
import { Interview } from '@/types/interview';
import { usePublishTimer } from './usePublishTimer';
import { useInterviewSelection } from './useInterviewSelection';
import { usePublishNotifications } from './usePublishNotifications';
import { useQueryClient } from '@tanstack/react-query';

interface UseAutoPublishProps {
  interviews: Interview[] | undefined;
  onPublish: (interviewId: string) => Promise<void>;
  publishInterval: number;
}

export function useAutoPublish({ interviews, onPublish, publishInterval }: UseAutoPublishProps) {
  const [isAutoPublish, setIsAutoPublish] = useState(false);
  const isPublishingRef = useRef(false);
  const publishTimeoutRef = useRef<number | null>(null);
  const queryClient = useQueryClient();
  
  const { 
    timeUntilNextPublish, 
    startCountdownTimer,
    resetTimer, 
    clearTimers 
  } = usePublishTimer({
    interval: publishInterval,
    isActive: isAutoPublish
  });
  
  const { 
    getInterviewsToPublish,
    getFeederDebugInfo
  } = useInterviewSelection();
  
  const {
    notifyAutoPublishSuccess,
    notifyAutoPublishError,
    notifyNoInterviewsToPublish,
    notifyAutoPublishToggle,
    notifyPublishLoading
  } = usePublishNotifications();

  // Process publishing one interview
  const processPublish = async () => {
    // Prevent concurrent publishing operations
    if (isPublishingRef.current) {
      console.log('Already publishing an interview, skipping this cycle');
      return;
    }

    try {
      // Debug log to show all interviews in the feeder
      if (interviews && interviews.length > 0) {
        console.log('Current interviews in the feeder:');
        interviews.forEach(interview => {
          console.log(`ID: ${interview.id}, Name: ${interview.intervieweeName}, Status: ${interview.publication_status}, In Feeder: ${interview.is_in_feeder}`);
        });
      }

      // Force a refetch of the feeder interviews to ensure we have the latest data
      await queryClient.invalidateQueries({ queryKey: ['feederInterviews'] });
      const refreshedData = await queryClient.fetchQuery({ queryKey: ['feederInterviews'] });
      
      // Use the refreshed data instead of the stale interviews prop
      const interviewsToPublish = getInterviewsToPublish(refreshedData as Interview[] || []);
      
      console.log(`Found ${interviewsToPublish.length} interviews to auto-publish after refresh`);
      
      if (interviewsToPublish.length > 0) {
        // Always publish the first interview in the list (top of the queue)
        const topInterview = interviewsToPublish[0];
        console.log('Auto-publishing top interview in queue:', topInterview.id, topInterview.intervieweeName);
        
        try {
          isPublishingRef.current = true;
          notifyPublishLoading(topInterview.intervieweeName);
          
          // Publish the interview
          await onPublish(topInterview.id);
          
          // Force invalidate and refetch all relevant query data
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['feederInterviews'] }),
            queryClient.invalidateQueries({ queryKey: ['publishedInterviews'] }),
            queryClient.invalidateQueries({ queryKey: ['interviews'] })
          ]);
          
          await Promise.all([
            queryClient.refetchQueries({ queryKey: ['feederInterviews'] }),
            queryClient.refetchQueries({ queryKey: ['publishedInterviews'] }),
            queryClient.refetchQueries({ queryKey: ['interviews'] })
          ]);
          
          notifyAutoPublishSuccess(topInterview.intervieweeName);
          console.log('Auto-publish successful for interview:', topInterview.id);
        } catch (error) {
          notifyAutoPublishError(error);
        } finally {
          isPublishingRef.current = false;
        }
      } else {
        const debugInfo = getFeederDebugInfo(refreshedData as Interview[] || []);
        notifyNoInterviewsToPublish(debugInfo);
      }
    } finally {
      // Always schedule the next publish cycle if auto-publish is still enabled
      if (isAutoPublish) {
        scheduleNextPublish();
      }
    }
  };

  // Schedule the next publish using setTimeout
  const scheduleNextPublish = () => {
    console.log(`Scheduling next auto-publish in ${publishInterval} seconds`);
    
    // Clear any existing timeouts first
    if (publishTimeoutRef.current) {
      window.clearTimeout(publishTimeoutRef.current);
    }
    
    // Schedule the next publish
    publishTimeoutRef.current = window.setTimeout(() => {
      console.log('Auto-publish timeout triggered');
      if (isAutoPublish) {
        processPublish();
      }
    }, publishInterval * 1000);
  };

  // Start auto-publish process
  const startAutoPublish = () => {
    console.log(`Starting auto-publish with interval of ${publishInterval} seconds`);
    
    // Start the countdown first
    startCountdownTimer();
    
    // Schedule the first publish after the specified interval
    scheduleNextPublish();
  };

  // Toggle auto-publish state
  const toggleAutoPublish = () => {
    const newState = !isAutoPublish;
    setIsAutoPublish(newState);
    notifyAutoPublishToggle(newState, publishInterval);
  };

  // React to changes in auto-publish state
  useEffect(() => {
    if (isAutoPublish) {
      console.log(`Auto-publishing enabled, will check every ${publishInterval} seconds`);
      startAutoPublish();
    } else {
      console.log('Auto-publishing disabled, clearing timers');
      // Clear the publish timeout
      if (publishTimeoutRef.current) {
        window.clearTimeout(publishTimeoutRef.current);
        publishTimeoutRef.current = null;
      }
      // Clear the countdown timer
      clearTimers();
    }
    
    // Cleanup function
    return () => {
      console.log('Cleaning up auto-publish hook');
      if (publishTimeoutRef.current) {
        window.clearTimeout(publishTimeoutRef.current);
        publishTimeoutRef.current = null;
      }
      clearTimers();
    };
  }, [isAutoPublish, publishInterval]);

  // React to changes in publish interval
  useEffect(() => {
    if (isAutoPublish) {
      console.log(`Publish interval changed to ${publishInterval} seconds, resetting timer`);
      resetTimer();
      
      // Also reset the publish schedule
      if (publishTimeoutRef.current) {
        window.clearTimeout(publishTimeoutRef.current);
        publishTimeoutRef.current = null;
      }
      
      // Schedule next publish with new interval
      scheduleNextPublish();
    }
  }, [publishInterval]);

  return {
    isAutoPublish,
    toggleAutoPublish,
    resetAutoPublishTimer: resetTimer,
    timeUntilNextPublish
  };
}
